import React, { useMemo, useState } from 'react'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import styled, { keyframes } from 'styled-components'
import { ink, surface, line, radius, font, bp, transition, cardStyle, labelStyle } from 'features/dashboard/tokens'
import SectionTitle from 'features/dashboard/components/SectionTitle'
import TasksRemedialsExplainer from 'features/taskManagement/TasksRemedialsExplainer'
import { taskStatusTint } from 'features/taskManagement/taskStatusConstants'
import { PRIVATE_PATHS } from 'utils/paths'
import TaskOriginPicker from 'features/taskManagement/components/TaskOriginPicker'
import ReportPeriodPicker, { resolvePeriod } from 'components/ReportPeriodPicker'
import BreakdownTable, { RAG, rateColour } from 'components/BreakdownTable'
import KpiReportDialog from 'features/dashboard/sections/KpiReportDialog'
import AssessmentRoundedIcon from '@mui/icons-material/AssessmentRounded'
import { DASHBOARD, involvesParams, remedialsViewUrl, resolvedDates } from 'features/actions/workspace/remedialsQuery'
import { RemedialCountsExplainer } from 'features/actions/workspace/Explainers'
import { RAG as SEV_RAG } from 'features/dashboard/charts/scoreColour'
import { hideEmail } from 'utils/user'
import InfoPopover, { PopLead } from 'features/actions/workspace/InfoPopover'
import { NOT_ENOUGH_DATA, RAISED_EXPLAIN, formatRate, rateExplanation } from 'features/actions/workspace/remedialPerformance'

const red = v => (v ? RAG.red : undefined)
const amber = v => (v ? RAG.amber : undefined)
const green = v => (v ? RAG.green : undefined)

const SITE_TASK_COLUMNS = [
  { key: 'total', label: 'Tasks', title: 'Tasks due in the period at this site' },
  { key: 'upcoming', label: 'Upcoming', title: 'Not finished yet, and not due for a while', drill: 'open=1' },
  { key: 'dueSoon', label: 'Due soon', title: 'Not finished yet, and due within the next few days', drill: 'open=1', colour: amber },
  { key: 'completed', label: 'Done', title: 'Finished', drill: 'status=completed', colour: green },
  { key: 'completedOnTime', label: 'On time', title: 'Finished on or before the due date', drill: 'status=completed', colour: green },
  { key: 'completedLate', label: 'Late', title: 'Finished after the due date', drill: 'completed_late=1', colour: amber },
  { key: 'overdue', label: 'Overdue', title: 'Not finished and past the due date', drill: 'overdue=1', colour: red },
  { key: 'unassigned', label: 'No one assigned', title: 'Open tasks with nobody responsible', colour: amber },
  { key: 'onTimeRate', label: 'On-time %', title: 'Share of finished tasks (with a due date) finished on time', percent: true, colour: rateColour },
  { key: 'completionRate', label: 'Done %', title: 'Share of tasks finished', percent: true },
  { key: 'avgDaysToComplete', label: 'Avg days', title: 'Average days from a task being created to it being finished' },
]

const REMEDIAL_COUNT_COLUMNS = [
  { key: 'live', label: 'Open', title: 'Remedials still being dealt with (no task yet, task created or task in progress)', drill: 'all_open' },
  { key: 'overdue', label: 'Overdue', title: 'Open remedials past their due date', colour: red, drill: 'overdue' },
  { key: 'awaitingReview', label: 'In review', title: 'Found by AI and waiting for someone to confirm them. Not counted as open yet.', colour: amber, drill: 'awaiting_review' },
  { key: 'opened', label: 'Raised', title: RAISED_EXPLAIN },
  { key: 'resolved', label: 'Resolved', title: 'Remedials resolved in the period', colour: green, drill: 'resolved' },
  { key: 'resolvedOnTime', label: 'On time', title: 'Resolved on or before the due date', colour: green },
  { key: 'resolvedLate', label: 'Late', title: 'Resolved after the due date', colour: amber },
  { key: 'onTimeRate', label: 'On-time %', title: 'Share of resolved remedials (with a due date) resolved on time', percent: true, colour: rateColour },
]

const SITE_REMEDIAL_COLUMNS = [
  ...REMEDIAL_COUNT_COLUMNS,
  ...['critical', 'high', 'medium', 'low'].map(sev => ({
    key: `sev_${sev}`,
    label: sev[0].toUpperCase() + sev.slice(1),
    title: `Open remedials rated ${sev}`,
    drill: 'all_open',
    severity: sev,
    colour: v => (v ? { critical: '#b00020', high: '#c2410c', medium: '#d97706', low: '#15803d' }[sev] : undefined),
  })),
]

const PEOPLE_COLUMNS = [
  { key: 'tasksAssigned', label: 'Tasks', title: 'Tasks assigned to this person that were due in the period' },
  { key: 'tasksCompleted', label: 'Done', title: 'Tasks finished', colour: green },
  { key: 'tasksCompletedOnTime', label: 'On time', title: 'Finished on or before the due date', colour: green },
  { key: 'tasksCompletedLate', label: 'Late', title: 'Finished after the due date', colour: amber },
  { key: 'tasksOverdue', label: 'Overdue', title: 'Not finished and past the due date', colour: red },
  { key: 'onTimeRate', label: 'On-time %', title: 'Share of their finished tasks that were on time', percent: true, colour: rateColour },
  { key: 'completionRate', label: 'Done %', title: 'Share of their tasks finished', percent: true },
  { key: 'avgDaysToComplete', label: 'Avg days', title: 'Average days to finish a task' },
  { key: 'remedialsOpen', label: 'Remedials owned', title: 'Open remedials this person owns' },
  { key: 'remedialsOverdue', label: 'Remedials overdue', title: 'Owned remedials past their due date', colour: red },
  { key: 'remedialsResolved', label: 'Remedials resolved', title: 'Owned remedials resolved in the period', colour: green },
]

const TASK_STATUS = {
  unassigned:        { label: 'Unassigned',         tint: taskStatusTint('unassigned') },
  assigned:          { label: 'Assigned',           tint: taskStatusTint('assigned') },
  pending_dependent: { label: 'Waiting on others',  tint: taskStatusTint('pending_dependent') },
  in_progress:       { label: 'In progress',        tint: taskStatusTint('in_progress') },
  on_hold:           { label: 'On hold',            tint: taskStatusTint('on_hold') },
  awaiting_approval: { label: 'Pending approval',   tint: taskStatusTint('awaiting_approval') },
  completed:         { label: 'Completed',          tint: taskStatusTint('completed') },
}
const ACTION_STATUS = {
  open:           { label: 'Open',           tint: SEV_RAG.high.fg },
  converted:      { label: 'In progress',    tint: SEV_RAG.medium.fg },
  resolved:       { label: 'Resolved',       tint: SEV_RAG.low.fg },
  dismissed:      { label: 'Dismissed',      tint: SEV_RAG.none.fg },
}
const SEVERITY = {
  critical: { label: 'Critical', tint: SEV_RAG.critical.fg },
  high:     { label: 'High',     tint: SEV_RAG.high.fg },
  medium:   { label: 'Medium',   tint: SEV_RAG.medium.fg },
  low:      { label: 'Low',      tint: SEV_RAG.low.fg },
}


const Donut = ({ size = 132, stroke = 12, segments = [], centerLabel, centerSub, onSegmentClick }) => {
  const r = (size - stroke) / 2
  const C = 2 * Math.PI * r
  const total = segments.reduce((a, s) => a + s.value, 0)
  let offset = 0
  return (
    <DonutWrap style={{ width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size/2} cy={size/2} r={r} fill='none' stroke={line.base} strokeWidth={stroke} />
        {total > 0 && segments.map((s, i) => {
          const length = (s.value / total) * C
          const dash = `${length} ${C - length}`
          const dashOffset = -offset
          offset += length
          const clickable = !!onSegmentClick && !!s.key
          return (
            <circle key={i}
              cx={size/2} cy={size/2} r={r}
              fill='none' stroke={s.tint}
              strokeWidth={stroke}
              strokeDasharray={dash}
              strokeDashoffset={dashOffset}
              role={clickable ? 'button' : undefined}
              tabIndex={clickable ? 0 : undefined}
              aria-label={clickable ? `${s.label}: ${s.value}` : undefined}
              onClick={clickable ? () => onSegmentClick(s) : undefined}
              onKeyDown={clickable ? e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onSegmentClick(s)
                }
              } : undefined}
              style={{ transition, cursor: clickable ? 'pointer' : 'default', outline: 'none' }} />
          )
        })}
      </svg>
      <DonutCenter>
        <DonutCenterValue>{centerLabel}</DonutCenterValue>
        {centerSub && <DonutCenterSub>{centerSub}</DonutCenterSub>}
      </DonutCenter>
    </DonutWrap>
  )
}

const StackBar = ({ buckets = [], onPick }) => {
  const total = buckets.reduce((a, b) => a + b.value, 0)
  if (total === 0) return <EmptyBar>No data</EmptyBar>
  return (
    <StackBarWrap>
      <BarTrack>
        {buckets.filter(b => b.value > 0).map((b, i) => (
          <BarSeg key={i}
            $tint={b.tint}
            style={{ width: `${(b.value / total) * 100}%` }}
            title={`${b.label}: ${b.value}`} />
        ))}
      </BarTrack>
      <BarLegend>
        {buckets.filter(b => b.value > 0).map((b, i) => {
          const clickable = !!onPick && !!b.key
          return (
            <LegendItem key={i}
              as={clickable ? 'button' : undefined}
              type={clickable ? 'button' : undefined}
              $clickable={clickable}
              aria-label={clickable ? `${b.label}: ${b.value}` : undefined}
              onClick={clickable ? () => onPick(b) : undefined}>
              <LegendDot $tint={b.tint} />
              <LegendLabel>{b.label}</LegendLabel>
              <LegendValue>{b.value}</LegendValue>
            </LegendItem>
          )
        })}
      </BarLegend>
    </StackBarWrap>
  )
}


const scopeParamFor = (scopeType, scopeId) => {
  if (!scopeId) return null
  if (scopeType === 'bp' || scopeType === 'unit') return { key: 'bp', value: scopeId }
  if (scopeType === 'estate') return { key: 'estate', value: scopeId }
  if (scopeType === 'portfolio') return { key: 'portfolio', value: scopeId }
  return null
}


const OpsInsightSection = ({ scopeLabel, insights, onFocusPerson, onClearLens, panel, scopeType, scopeId, range, rangeText, onRangeChange, kpiInitial, multiSite = true, origin, onOriginChange, assigneeId = null }) => {
  const [kpiOpen, setKpiOpen] = useState(false)
  const siteTasksUrl = (bpId, extra) => {
    const q = new URLSearchParams({ tab: 'tasks', bp: bpId })
    const r = resolvePeriod(range)
    if (r.from) { q.set('deadline_from', r.from); q.set('deadline_to', r.to) }
    if (origin) q.set('origin', origin)
    return `${PRIVATE_PATHS.taskManagement}?${q.toString()}${extra ? `&${extra}` : ''}`
  }
  const navigate = useNavigate()
  const [tab, setTab] = useState('tasks')
  const activeTab = panel || tab
  const scopeParam = scopeParamFor(scopeType, scopeId)
  const withScope = (basePath, query = {}) => {
    const params = new URLSearchParams(query)
    if (scopeParam) params.set(scopeParam.key, scopeParam.value)
    const qs = params.toString()
    return qs ? `${basePath}?${qs}` : basePath
  }
  const goTasks = query => navigate(withScope('/tasks', query))
  const goIncidents = query => navigate(withScope('/incidents', query))
  const isBpScoped = (scopeType === 'bp' || scopeType === 'unit') && !!scopeId
  const remedialsScopeParam = (() => {
    if (isBpScoped) return null
    if (scopeType === 'estate' && scopeId) return { key: 'estate', value: scopeId }
    if (scopeType === 'portfolio' && scopeId) return { key: 'portfolio', value: scopeId }
    return null
  })()
  const lens = insights?.lens || null
  const personExtra = involvesParams(assigneeId || lens?.userId || null, lens?.name)
  const remedialsUrl = (view, extra = {}) => (isBpScoped
    ? remedialsViewUrl({ bpId: scopeId }, view, { ...personExtra, ...extra })
    : remedialsViewUrl({}, view, { ...(remedialsScopeParam ? { [remedialsScopeParam.key]: remedialsScopeParam.value } : {}), ...personExtra, ...extra }))
  const goRemedials = (view = DASHBOARD, extra = {}) => navigate(remedialsUrl(view, extra))
  const goRemedialReview = () => navigate(isBpScoped ? PRIVATE_PATHS.remedialsReview.replace(':bpId', scopeId) : PRIVATE_PATHS.actionsReview)
  const periodRange = resolvePeriod(range)
  const lastThirtyDays = { from: moment().subtract(30, 'days').format('YYYY-MM-DD'), to: moment().format('YYYY-MM-DD') }
  const remedialColumnExtra = (column, dates) => {
    const view = column?.drill || 'all_open'
    return { view, extra: { ...(column?.severity ? { severity: column.severity } : {}), ...resolvedDates(view, dates) } }
  }
  const goSiteRemedials = (row, column) => {
    const { view, extra } = remedialColumnExtra(column, periodRange)
    navigate(remedialsViewUrl({ bpId: row.bpId }, view, { ...personExtra, ...extra }))
  }
  const goOwnerRemedials = (row, column) => {
    if (!row.userId) return
    const { view, extra } = remedialColumnExtra(column, periodRange)
    goRemedials(view, { owner: row.userId, ...extra })
  }
  const t = insights?.tasks || {}
  const a = insights?.actions || {}
  const i = insights?.incidents || {}
  const people = insights?.people || []

  const taskSegments = useMemo(() => {
    const byStatus = t.byStatus || {}
    return Object.entries(TASK_STATUS).map(([k, meta]) => ({
      key:   k,
      label: meta.label,
      tint:  meta.tint,
      value: byStatus[k] || 0,
    }))
  }, [t.byStatus])

  const taskBuckets = useMemo(() => {
    const byStatus = t.byStatus || {}
    return Object.entries(TASK_STATUS).map(([k, meta]) => ({
      key:   k,
      label: meta.label,
      tint:  meta.tint,
      value: byStatus[k] || 0,
    }))
  }, [t.byStatus])

  const actionBuckets = useMemo(() => {
    const byStatus = a.byStatus || {}
    return Object.entries(ACTION_STATUS).map(([k, meta]) => ({
      key:   k,
      label: meta.label,
      tint:  meta.tint,
      value: byStatus[k] || 0,
    }))
  }, [a.byStatus])

  const actionSeverity = useMemo(() => {
    const bySev = a.bySeverity || {}
    return Object.entries(SEVERITY).map(([k, meta]) => ({
      key:   k,
      label: meta.label,
      tint:  meta.tint,
      value: bySev[k] || 0,
    }))
  }, [a.bySeverity])

  const incidentSeverity = useMemo(() => {
    const bySev = i.bySeverity || {}
    return Object.entries(SEVERITY).map(([k, meta]) => ({
      key:   k,
      label: meta.label,
      tint:  meta.tint,
      value: bySev[k] || 0,
    }))
  }, [i.bySeverity])

  if (!insights) return null

  return (
    <Wrap $embedded={!!panel}>
      {insights?.scope?.level === 'unit' && (
        <UnitScopeBadge title='These figures cover this Unit Passport’s own tasks & remedials, plus the building-wide items that also apply to it. Tasks tied to other units are excluded.'>
          This Unit Passport + building-wide
        </UnitScopeBadge>
      )}
      {onRangeChange && (!panel || ['tasks', 'actions', 'people'].includes(panel)) && (
        <Toolbar>
          <ReportPeriodPicker value={range} onChange={onRangeChange} label={activeTab === 'actions' ? 'Raised or resolved' : 'Tasks due'} />
          {kpiInitial && (
            <ReportBtn type='button' onClick={() => setKpiOpen(true)} title='Download a KPI report with exactly these sites, dates, task type and person'>
              <AssessmentRoundedIcon style={{ fontSize: 17 }} />
              Download report
            </ReportBtn>
          )}
          {kpiInitial && <KpiReportDialog open={kpiOpen} onClose={() => setKpiOpen(false)} initial={kpiInitial} />}
        </Toolbar>
      )}
      {onRangeChange && (!panel || ['tasks', 'actions', 'people'].includes(panel)) && (
        <RangeNote>
          {activeTab === 'actions'
            ? `Raised and resolved figures cover ${rangeText}. Raised includes remedials still awaiting review. Open, overdue and in-review figures are as they stand today.`
            : `Counting tasks due ${rangeText === 'all time' ? 'at any time' : `between ${String(rangeText).replace(' to ', ' and ')}`}.`}
        </RangeNote>
      )}
      {onOriginChange && (!panel || panel === 'tasks' || panel === 'people') && activeTab !== 'actions' && (
        <OriginRow>
          <TaskOriginPicker value={origin || null} onChange={onOriginChange} />
        </OriginRow>
      )}
      {panel ? (
        lens ? (
          <EmbeddedControls>
            <LensChip onClick={onClearLens} title='Clear responsible-party lens'>
              Showing: {lens.name} ×
            </LensChip>
          </EmbeddedControls>
        ) : null
      ) : (
        <SectionTitle
          eyebrow={scopeLabel || undefined}
          action={(
            <HeadControls>
            <TasksRemedialsExplainer align='right' />
            {lens && (
              <LensChip onClick={onClearLens} title='Clear responsible-party lens'>
                Showing: {lens.name} ×
              </LensChip>
            )}
            <TabBar role='tablist'>
              {[
                { k: 'tasks',     label: `Tasks · ${t.total ?? 0}` },
                { k: 'actions',   label: `Remedials · ${a.total ?? 0}` },
                { k: 'incidents', label: `Incidents · ${i.total ?? 0}` },
                { k: 'people',    label: `People · ${people.length}` },
              ].map(tt => (
                <Tab key={tt.k} $active={tab === tt.k} onClick={() => setTab(tt.k)}>{tt.label}</Tab>
              ))}
            </TabBar>
            </HeadControls>
          )}
        >
          Tasks, remedials &amp; people
        </SectionTitle>
      )}

      {activeTab === 'tasks' && (
        <>
        <Grid $flush={!!panel}>
          <Card $flush={!!panel}>
            <CardHead>Task status</CardHead>
            <DonutRow>
              <Donut
                segments={taskSegments}
                centerLabel={t.total ?? 0}
                centerSub='tasks'
                onSegmentClick={s => goTasks({ status: s.key })} />
              <KpiList>
                <KpiTile
                  $tint='#b00020'
                  $clickable
                  role='button'
                  tabIndex={0}
                  aria-label='Overdue tasks'
                  onClick={() => goTasks({ status: 'overdue' })}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      goTasks({ status: 'overdue' })
                    }
                  }}>
                  <KpiValue>{t.overdue ?? 0}</KpiValue>
                  <KpiLabel>Overdue</KpiLabel>
                </KpiTile>
                <KpiTile $tint='#15803d' title='Tasks completed on or before their deadline.'>
                  <KpiValue>{t.completedOnTime ?? 0}</KpiValue>
                  <KpiLabel>Completed on time</KpiLabel>
                </KpiTile>
                <KpiTile $tint='#d97706'>
                  <KpiValue>{t.completedLate ?? 0}</KpiValue>
                  <KpiLabel>Completed late</KpiLabel>
                </KpiTile>
                <KpiTile $tint='#15803d' title='Of the tasks completed, the share finished on or before their deadline.'>
                  <KpiValue $muted={t.onTimeRate == null}>{formatRate(t.onTimeRate)}</KpiValue>
                  <KpiLabel>On-time rate</KpiLabel>
                </KpiTile>
                <KpiTile $tint='#2aa6b3' title='The share of tasks due in these dates that have been completed.'>
                  <KpiValue $muted={!t.total}>{t.total ? `${t.completionRate ?? 0}%` : NOT_ENOUGH_DATA}</KpiValue>
                  <KpiLabel>Completion</KpiLabel>
                </KpiTile>
                <KpiTile $tint='#7C5FE0' title='Average number of days from a task being raised to it being completed (completed tasks only). Tasks brought across from the previous system are left out, because it did not record when they were raised.'>
                  <KpiValue $muted={t.avgDaysToComplete == null}>{t.avgDaysToComplete ?? NOT_ENOUGH_DATA}</KpiValue>
                  <KpiLabel>Avg days to complete</KpiLabel>
                </KpiTile>
              </KpiList>
            </DonutRow>
            <StackBar buckets={taskBuckets} onPick={b => goTasks({ status: b.key })} />
          </Card>
          <Card $flush={!!panel}>
            <CardHead>By task domain</CardHead>
            {(t.byDomain || []).length === 0
              ? <Empty>No tasks scoped yet.</Empty>
              : (
                <DomainList>
                  {t.byDomain.map(d => {
                    const pct = d.total > 0 ? Math.round((d.completed / d.total) * 100) : 0
                    const tint = d.colour || '#33becc'
                    const clickable = !!d.domainId
                    return (
                      <DomainRow
                        key={d.domainId || d.name}
                        as={clickable ? 'button' : undefined}
                        type={clickable ? 'button' : undefined}
                        $clickable={clickable}
                        aria-label={clickable ? `${d.short || d.name} tasks` : undefined}
                        onClick={clickable ? () => goTasks({ domain: d.domainId }) : undefined}>
                        <DomainLabel>
                          <DomainSwatch $tint={tint} />
                          <span>{d.short || d.name}</span>
                        </DomainLabel>
                        <DomainBar>
                          <DomainBarFill style={{ width: `${pct}%`, background: tint }} />
                        </DomainBar>
                        <DomainStats>
                          <DomainStatTotal>{d.total}</DomainStatTotal>
                          {d.overdue > 0 && <DomainStatOverdue>{d.overdue} overdue</DomainStatOverdue>}
                        </DomainStats>
                      </DomainRow>
                    )
                  })}
                </DomainList>
              )}
            <CardFootnote>
              Last 30 days · {t.last30d?.opened ?? 0} opened · {t.last30d?.completed ?? 0} completed
            </CardFootnote>
          </Card>
        </Grid>
        {multiSite && (t.byBuilding || []).length > 0 && (
          <Breakdowns>
            <BreakdownTable
              title={`Tasks by site (${t.byBuilding.length})`}
              helpText='Every Building Passport in this view. Click a site or a number to open those tasks.'
              rows={t.byBuilding}
              columns={SITE_TASK_COLUMNS}
              nameLabel='Site'
              rowKey={r => r.bpId}
              defaultSort='overdue'
              searchPlaceholder='Search sites…'
              csvName='tasks-by-site'
              onRowClick={(r, col) => navigate(siteTasksUrl(r.bpId, col?.drill))}
            />
          </Breakdowns>
        )}
        </>
      )}

      {activeTab === 'actions' && (
        <>
        <Grid $flush={!!panel}>
          <Card $flush={!!panel}>
            <CardHead>Remedial status <RemedialCountsExplainer withRecent /></CardHead>
            <DonutRow>
              <Donut
                segments={actionBuckets}
                centerLabel={a.total ?? 0}
                centerSub='remedials'
                onSegmentClick={() => goRemedials()} />
              <KpiList>
                {[
                  { key: 'allOpen', label: 'Open now', value: a.views?.allOpen ?? 0, tint: SEV_RAG.medium.fg, view: 'all_open', aria: 'Open remedials' },
                  { key: 'overdue', label: 'Overdue', value: a.views?.overdue ?? a.overdue ?? 0, tint: SEV_RAG.critical.fg, view: 'overdue', aria: 'Overdue remedials' },
                  { key: 'needsDecision', label: 'Needs a decision', value: a.views?.needsDecision ?? 0, tint: SEV_RAG.high.fg, view: 'needs_decision', aria: 'Remedials that need a decision' },
                  { key: 'dueThisWeek', label: 'Due this week', value: a.views?.dueThisWeek ?? 0, tint: SEV_RAG.medium.fg, view: 'due_this_week', aria: 'Remedials due this week' },
                  { key: 'recentlyResolved', label: 'Resolved, last 30 days', value: a.views?.recentlyResolved ?? 0, tint: SEV_RAG.low.fg, view: 'resolved', extra: resolvedDates('resolved', lastThirtyDays), aria: 'Remedials resolved in the last 30 days' },
                  { key: 'recentlyRaised', label: 'Raised, last 30 days', value: a.views?.recentlyRaised ?? 0, tint: '#33becc', view: DASHBOARD, aria: 'Remedials raised in the last 30 days', hint: 'Remedials raised in the last 30 days, from any source, including ones still awaiting review and ones later dismissed.' },
                ].map(tile => (
                  <KpiTile
                    key={tile.key}
                    $tint={tile.tint}
                    $clickable
                    role='button'
                    tabIndex={0}
                    aria-label={`${tile.aria}: ${tile.value}. Open the list`}
                    title={tile.hint}
                    onClick={() => goRemedials(tile.view, tile.extra)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        goRemedials(tile.view, tile.extra)
                      }
                    }}>
                    <KpiValue>{tile.value}</KpiValue>
                    <KpiLabel>{tile.label}</KpiLabel>
                  </KpiTile>
                ))}
                {(a.inReview ?? 0) > 0 && (
                  <KpiTile
                    $full
                    $tint={SEV_RAG.medium.fg}
                    $clickable
                    role='button'
                    tabIndex={0}
                    aria-label='Remedials awaiting review'
                    onClick={() => goRemedialReview()}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        goRemedialReview()
                      }
                    }}>
                    <KpiValue>{a.inReview}</KpiValue>
                    <KpiLabel>Awaiting review, not counted as open yet. Tap to check them</KpiLabel>
                  </KpiTile>
                )}
              </KpiList>
            </DonutRow>
            <StackBar buckets={actionBuckets} onPick={() => goRemedials()} />
          </Card>
          <Card $flush={!!panel}>
            <CardHead>By severity</CardHead>
            <SeverityGrid>
              {actionSeverity.map(s => (
                <SeverityCell
                  key={s.key}
                  $tint={s.tint}
                  $clickable
                  role='button'
                  tabIndex={0}
                  aria-label={`${s.label} open remedials: ${s.value}. Open the list`}
                  onClick={() => goRemedials('all_open', { severity: s.key })}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goRemedials('all_open', { severity: s.key }) } }}
                >
                  <SeverityValue>{s.value}</SeverityValue>
                  <SeverityLabel>{s.label}</SeverityLabel>
                </SeverityCell>
              ))}
            </SeverityGrid>
            <CardFootnote>Counts open remedials only. Remedials still in review are not counted until someone confirms them.</CardFootnote>
          </Card>
        </Grid>
        {a.inPeriod && (
          <PeriodStats aria-label='Remedials in the chosen period'>
            <PeriodStat title={RAISED_EXPLAIN}><strong>{a.inPeriod.opened}</strong><span>raised</span></PeriodStat>
            <PeriodStat
              as='button'
              type='button'
              $tint='#15803d'
              $clickable
              aria-label={`Remedials resolved in the chosen period: ${a.inPeriod.resolved}. Open the list`}
              onClick={() => goRemedials('resolved', resolvedDates('resolved', periodRange))}
            >
              <strong>{a.inPeriod.resolved}</strong><span>resolved</span>
            </PeriodStat>
            <PeriodStat $tint='#15803d'><strong>{a.inPeriod.resolvedOnTime}</strong><span>resolved on time</span></PeriodStat>
            <PeriodStat $tint='#d97706'><strong>{a.inPeriod.resolvedLate}</strong><span>resolved late</span></PeriodStat>
            <PeriodStat $tint={rateColour(a.inPeriod.onTimeRate)}>
              <strong className={a.inPeriod.onTimeRate == null ? 'muted' : undefined}>{formatRate(a.inPeriod.onTimeRate)}</strong>
              <span>
                on-time rate
                <InfoPopover label='What does on-time rate mean?' title='On-time rate'>
                  <PopLead>{rateExplanation(a.inPeriod)}</PopLead>
                </InfoPopover>
              </span>
            </PeriodStat>
          </PeriodStats>
        )}
        <Breakdowns>
          {multiSite && (a.byBuilding || []).length > 0 && (
            <BreakdownTable
              title={`Remedials by site (${a.byBuilding.length})`}
              helpText='Open, overdue and severity are as they stand today. Raised and resolved cover the chosen dates. Click a site to open its remedials.'
              rows={a.byBuilding.map(r => ({ ...r, sev_critical: r.bySeverity?.critical, sev_high: r.bySeverity?.high, sev_medium: r.bySeverity?.medium, sev_low: r.bySeverity?.low }))}
              columns={SITE_REMEDIAL_COLUMNS}
              nameLabel='Site'
              rowKey={r => r.bpId}
              defaultSort='overdue'
              searchPlaceholder='Search sites…'
              csvName='remedials-by-site'
              onRowClick={goSiteRemedials}
            />
          )}
          {(a.byOwner || []).length > 0 && (
            <BreakdownTable
              title={`Remedials by owner (${a.byOwner.length})`}
              helpText='The person responsible for seeing each remedial through. Click a name or a number to open their remedials.'
              rows={a.byOwner.map(r => ({ ...r, ownerKey: r.userId || r.name }))}
              columns={REMEDIAL_COUNT_COLUMNS}
              nameLabel='Owner'
              rowKey={r => r.ownerKey}
              defaultSort='overdue'
              searchPlaceholder='Search people…'
              csvName='remedials-by-owner'
              onRowClick={goOwnerRemedials}
              isRowClickable={r => !!r.userId}
            />
          )}
        </Breakdowns>
        </>
      )}

      {activeTab === 'incidents' && (
        <Grid $flush={!!panel}>
          <Card $flush={!!panel}>
            <CardHead>Open vs closed</CardHead>
            <DonutRow>
              <Donut
                segments={[
                  { key: 'open',   label: 'Open',   tint: '#b00020', value: i.open ?? 0 },
                  { key: 'closed', label: 'Closed', tint: '#2aa6b3', value: i.closed ?? 0 },
                ]}
                centerLabel={i.total ?? 0}
                centerSub='incidents'
                onSegmentClick={s => goIncidents({ status: s.key })} />
              <KpiList>
                <KpiTile
                  $tint='#b00020'
                  $clickable
                  role='button'
                  tabIndex={0}
                  aria-label='Open incidents'
                  onClick={() => goIncidents({ status: 'open' })}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      goIncidents({ status: 'open' })
                    }
                  }}>
                  <KpiValue>{i.open ?? 0}</KpiValue>
                  <KpiLabel>Open</KpiLabel>
                </KpiTile>
                <KpiTile
                  $tint='#2aa6b3'
                  $clickable
                  role='button'
                  tabIndex={0}
                  aria-label='Closed incidents'
                  onClick={() => goIncidents({ status: 'closed' })}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      goIncidents({ status: 'closed' })
                    }
                  }}>
                  <KpiValue>{i.closed ?? 0}</KpiValue>
                  <KpiLabel>Closed</KpiLabel>
                </KpiTile>
                <KpiTile $tint='#33becc'>
                  <KpiValue>{i.last30d?.opened ?? 0}</KpiValue>
                  <KpiLabel>Opened 30d</KpiLabel>
                </KpiTile>
                <KpiTile $tint='#7C5FE0'>
                  <KpiValue>{i.last30d?.closed ?? 0}</KpiValue>
                  <KpiLabel>Closed 30d</KpiLabel>
                </KpiTile>
              </KpiList>
            </DonutRow>
          </Card>
          <Card $flush={!!panel}>
            <CardHead>By severity</CardHead>
            <SeverityGrid>
              {incidentSeverity.map(s => (
                <SeverityCell
                  key={s.label}
                  $tint={s.tint}
                  $clickable
                  role='button'
                  tabIndex={0}
                  aria-label={`${s.label} severity incidents`}
                  onClick={() => goIncidents({ severity: s.key })}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      goIncidents({ severity: s.key })
                    }
                  }}>
                  <SeverityValue>{s.value}</SeverityValue>
                  <SeverityLabel>{s.label}</SeverityLabel>
                </SeverityCell>
              ))}
            </SeverityGrid>
          </Card>
        </Grid>
      )}

      {activeTab === 'people' && (
        people.length === 0 ? (
          <Card $flush={!!panel}>
            <CardHead>People</CardHead>
            <Empty>
              <strong>No tasks are assigned to anyone here yet.</strong>
              <br />
              Assign a task to a building manager or accountable person (from the task's page) and you'll see each person's progress here. Contractors have their own Contractor performance widget.
            </Empty>
          </Card>
        ) : (
          <BreakdownTable
            title={`People (${people.length})`}
            helpText='Tasks assigned to each person that were due in the chosen dates, plus the remedials they own. Click a name to see their tasks.'
            rows={people.map(p => ({ ...p, personKey: p.userId || p.email || p.name, name: p.isExternal ? `${hideEmail(p.name || '')} (external)` : hideEmail(p.name || '') }))}
            columns={PEOPLE_COLUMNS}
            nameLabel='Person'
            rowKey={r => r.personKey}
            defaultSort='tasksOverdue'
            searchPlaceholder='Search people…'
            csvName='people-performance'
            onRowClick={r => r.userId && onFocusPerson && onFocusPerson(r.userId)}
          />
        )
      )}
    </Wrap>
  )
}

export default OpsInsightSection

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`

const Wrap = styled.section`
  margin-bottom: ${({ $embedded }) => ($embedded ? '0' : '28px')};
  animation: ${fadeUp} 280ms ease both;
  ${({ $embedded }) => $embedded && `
    min-height: 100%;
    display: flex;
    flex-direction: column;
  `}
`

const EmbeddedControls = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
`

const TabBar = styled.div`
  display: inline-flex; gap: 4px;
  padding: 4px;
  background: ${surface.wash};
  border-radius: ${radius.pill};
  @media (max-width: 600px) {
    display: flex;
    max-width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    &::-webkit-scrollbar { display: none; }
    & > button { flex-shrink: 0; }
  }
`
const Tab = styled.button`
  font: inherit;
  padding: 6px 14px;
  background: ${({ $active, theme }) => $active ? theme.colors.haiti : 'transparent'};
  color: ${({ $active, theme }) => $active ? '#fff' : theme.colors.haiti};
  border: none;
  border-radius: ${radius.pill};
  font-size: ${font.small};
  font-weight: 800;
  letter-spacing: 0.03em;
  cursor: pointer;
  transition: ${transition};
  &:hover { background: ${({ $active, theme }) => $active ? theme.colors.haiti : line.base}; }
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: ${({ $flush }) => ($flush ? '1fr' : 'minmax(0, 1.3fr) minmax(0, 1fr)')};
  gap: ${({ $flush }) => ($flush ? '18px' : '14px')};
  @media (max-width: ${bp.tablet}) { grid-template-columns: 1fr; }
`

const Card = styled.div`
  ${({ $flush }) => ($flush ? '' : cardStyle)}
  padding: ${({ $flush }) => ($flush ? '0' : '20px 22px')};
  display: flex; flex-direction: column; gap: 14px;
  ${({ $fill }) => $fill && 'flex: 1; min-height: 0;'}
`

const CardHead = styled.h4`
  margin: 0;
  ${labelStyle}
`

const CardFootnote = styled.div`
  font-size: ${font.caption};
  color: ${({ theme }) => theme.colors.grey[800]};
  border-top: 1px dashed ${line.base};
  padding-top: 8px;
`

const DonutRow = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 16px;
  align-items: center;
  @media (max-width: 560px) {
    grid-template-columns: 1fr;
    justify-items: center;
  }
`

const DonutWrap = styled.div`
  position: relative;
  display: inline-flex; align-items: center; justify-content: center;
`
const DonutCenter = styled.div`
  position: absolute;
  display: flex; flex-direction: column; align-items: center;
`
const DonutCenterValue = styled.span`
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: ${font.display};
  letter-spacing: -0.6px;
  color: ${({ theme }) => theme.colors.haiti};
  line-height: 1;
  font-variant-numeric: tabular-nums;
`
const DonutCenterSub = styled.span`
  font-size: ${font.caption}; letter-spacing: 0.10em; text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-weight: 700;
  margin-top: 4px;
`

const KpiList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
`
const KpiTile = styled.div`
  display: flex; flex-direction: column; gap: 2px;
  padding: 8px 12px;
  background: ${surface.card};
  border: 1px solid ${line.base};
  border-left: 3px solid ${({ $tint }) => $tint};
  border-radius: ${radius.sm};
  ${({ $full }) => $full && 'grid-column: 1 / -1;'}
  ${({ $clickable }) => $clickable && `
    cursor: pointer;
    text-align: left;
    font: inherit;
    transition: ${transition};
    &:hover { background: ${surface.wash}; border-color: ${ink.mute}; }
    &:focus-visible { outline: 2px solid ${ink.mute}; outline-offset: 1px; }
  `}
`
const KpiValue = styled.span`
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: ${({ $muted }) => ($muted ? '15px' : font.heading)};
  letter-spacing: ${({ $muted }) => ($muted ? '0' : '-0.4px')};
  color: ${({ $muted, theme }) => ($muted ? '#667085' : theme.colors.haiti)};
  line-height: ${({ $muted }) => ($muted ? 1.25 : 1)};
  font-variant-numeric: tabular-nums;
`
const KpiLabel = styled.span`
  font-size: ${font.caption};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-weight: 700;
`

const StackBarWrap = styled.div` display: flex; flex-direction: column; gap: 6px; `
const BarTrack = styled.div`
  display: flex;
  height: 10px;
  background: ${surface.wash};
  border-radius: ${radius.pill};
  overflow: hidden;
`
const BarSeg = styled.div`
  background: ${({ $tint }) => $tint};
  height: 100%;
  transition: ${transition};
`
const BarLegend = styled.div`
  display: flex; flex-wrap: wrap; gap: 4px 12px;
`
const LegendItem = styled.span`
  display: inline-flex; align-items: center; gap: 6px;
  ${({ $clickable }) => $clickable && `
    cursor: pointer;
    border: 1px solid transparent;
    background: transparent;
    padding: 2px 6px;
    margin: -2px -6px;
    border-radius: ${radius.pill};
    font: inherit;
    transition: ${transition};
    &:hover { background: ${surface.wash}; border-color: ${line.base}; }
    &:focus-visible { outline: 2px solid ${ink.mute}; outline-offset: 1px; }
  `}
`
const LegendDot = styled.span`
  width: 8px; height: 8px; border-radius: ${radius.round};
  background: ${({ $tint }) => $tint};
`
const LegendLabel = styled.span`
  font-size: ${font.caption};
  color: ${({ theme }) => theme.colors.grey[800]};
`
const LegendValue = styled.span`
  font-size: ${font.caption};
  font-weight: 800;
  color: ${({ theme }) => theme.colors.haiti};
  font-variant-numeric: tabular-nums;
`
const EmptyBar = styled.div`
  padding: 12px;
  text-align: center;
  font-size: ${font.small};
  color: ${({ theme }) => theme.colors.grey[800]};
  border: 1px dashed ${line.base};
  border-radius: ${radius.sm};
`

const DomainList = styled.div` display: flex; flex-direction: column; gap: 8px; `
const DomainRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.5fr auto;
  gap: 10px;
  align-items: center;
  ${({ $clickable }) => $clickable && `
    width: 100%;
    text-align: left;
    font: inherit;
    cursor: pointer;
    background: transparent;
    border: 1px solid transparent;
    border-radius: ${radius.sm};
    padding: 4px 6px;
    margin: -4px -6px;
    transition: ${transition};
    &:hover { background: ${surface.wash}; border-color: ${line.base}; }
    &:focus-visible { outline: 2px solid ${ink.mute}; outline-offset: 1px; }
  `}
`
const DomainLabel = styled.div`
  display: flex; align-items: center; gap: 8px;
  font-size: ${font.body};
  color: ${({ theme }) => theme.colors.haiti};
  font-weight: 600;
  min-width: 0;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
`
const DomainSwatch = styled.span`
  width: 8px; height: 16px;
  background: ${({ $tint }) => $tint};
  border-radius: ${radius.xs};
  flex-shrink: 0;
`
const DomainBar = styled.div`
  height: 8px;
  background: ${surface.wash};
  border-radius: ${radius.pill};
  overflow: hidden;
`
const DomainBarFill = styled.div` height: 100%; transition: ${transition}; border-radius: ${radius.pill}; `
const DomainStats = styled.div`
  display: inline-flex; align-items: center; gap: 8px;
  font-variant-numeric: tabular-nums;
`
const DomainStatTotal = styled.span`
  font-size: ${font.small};
  font-weight: 800;
  color: ${({ theme }) => theme.colors.haiti};
`
const DomainStatOverdue = styled.span`
  font-size: ${font.caption};
  font-weight: 800;
  color: #b00020;
  padding: 2px 6px;
  background: #b0002015;
  border: 1px solid #b0002035;
  border-radius: ${radius.pill};
`

const SeverityGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
`
const SeverityCell = styled.div`
  display: flex; flex-direction: column; align-items: center; gap: 2px;
  padding: 10px 8px;
  background: ${({ $tint }) => `${$tint}12`};
  border: 1px solid ${({ $tint }) => `${$tint}30`};
  border-radius: ${radius.md};
  ${({ $clickable, $tint }) => $clickable && `
    cursor: pointer;
    transition: ${transition};
    &:hover { background: ${$tint}20; border-color: ${$tint}55; }
    &:focus-visible { outline: 2px solid ${$tint}; outline-offset: 1px; }
  `}
`
const SeverityValue = styled.span`
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: ${font.title};
  letter-spacing: -0.4px;
  color: ${({ theme }) => theme.colors.haiti};
  line-height: 1;
  font-variant-numeric: tabular-nums;
`
const SeverityLabel = styled.span`
  font-size: ${font.caption};
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const HeadControls = styled.div`
  display: inline-flex; align-items: center; gap: 10px;
  flex-wrap: wrap;
`

const UnitScopeBadge = styled.div`
  display: inline-flex; align-items: center;
  width: fit-content;
  margin-bottom: 10px;
  padding: 4px 11px;
  border-radius: ${radius.pill};
  background: ${surface.wash};
  border: 1px solid ${line.strong};
  color: ${({ theme }) => theme.colors.grey[800]};
  font-size: ${font.caption};
  font-weight: 800;
`

const OriginRow = styled.div`
  display: flex; margin: 0 0 12px; max-width: 100%;
`

const LensChip = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 14px;
  background: rgba(51, 190, 204, 0.10);
  color: #1f8c97;
  border: 1px solid rgba(51, 190, 204, 0.32);
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
  &:hover { background: rgba(51, 190, 204, 0.18); }
`

const Empty = styled.div`
  padding: 20px;
  text-align: center;
  border: 1px dashed ${line.base};
  border-radius: ${radius.md};
  font-size: ${font.body};
  color: ${({ theme }) => theme.colors.grey[800]};
`

const Toolbar = styled.div`
  display: flex; align-items: center; flex-wrap: wrap; gap: 10px; margin-bottom: 8px;
`

const ReportBtn = styled.button`
  font: inherit; display: inline-flex; align-items: center; gap: 6px;
  min-height: 40px; padding: 8px 14px; border-radius: 10px; cursor: pointer;
  border: 1px solid #33becc; background: #33becc; color: #fff; font-size: 13.5px; font-weight: 700;
  &:hover { background: #2aa6b3; }
  &:focus-visible { outline: 2px solid #1d7f89; outline-offset: 2px; }
`

const RangeNote = styled.p`
  margin: 0 0 12px; font-size: 13px; color: #475467; line-height: 1.45;
`

const Breakdowns = styled.div`
  display: flex; flex-direction: column; gap: 14px; margin-top: 14px; min-width: 0;
`

const PeriodStats = styled.div`
  display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 10px; margin-top: 14px;
  @media (max-width: 768px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
`

const PeriodStat = styled.div`
  display: flex; flex-direction: column; gap: 2px; padding: 10px 12px; border-radius: 10px;
  border: 1px solid #eceef3; background: #fcfcfe;
  font: inherit; text-align: left;
  ${({ $clickable }) => $clickable && `
    cursor: pointer;
    min-height: 40px;
    &:hover { border-color: #33becc; }
    &:focus-visible { outline: 2px solid #33becc; outline-offset: 2px; }
  `}
  strong { font-size: 20px; font-weight: 800; color: ${({ $tint }) => $tint || '#101828'}; }
  strong.muted { font-size: 14px; font-weight: 600; color: #667085; }
  span { font-size: 12.5px; color: #667085; }
`
